// Simple global font loader that ensures custom fonts are loaded on app initialization
import { getCustomFontFamilies } from '@/Utility/customFontHelper';

// Cache to track which custom fonts have been loaded globally
const globalLoadedFonts = new Set<string>();

/**
 * Load custom font globally by creating @font-face declarations
 */
export const loadCustomFontGlobally = (familyName: string): void => {
  // Check cache first to avoid unnecessary work
  if (globalLoadedFonts.has(familyName)) {
    return;
  }

  const fontFamilies = getCustomFontFamilies();
  const fontFamily = fontFamilies[familyName];

  if (!fontFamily || !fontFamily.variants) return;

  // Create @font-face declarations for all variants of this family
  const fontFaces = fontFamily.variants
    .map((variant: { url: string; weight: number; style: string }) => {
      const format = variant.url.endsWith('.woff2')
        ? 'woff2'
        : variant.url.endsWith('.woff')
          ? 'woff'
          : variant.url.endsWith('.ttf')
            ? 'truetype'
            : 'opentype';

      return `
        @font-face {
          font-family: '${familyName}';
          src: url('${variant.url}') format('${format}');
          font-weight: ${variant.weight};
          font-style: ${variant.style};
          font-display: swap;
        }
      `;
    })
    .join('');

  // Only inject styles if not already present
  const existingStyle = document.getElementById(`global-custom-font-${familyName}`);
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = `global-custom-font-${familyName}`;
    style.setAttribute('nonce', window.znRuntime?.cspStrictValue || '');
    style.textContent = fontFaces;
    document.head.appendChild(style);

    // Add to cache to prevent future re-injection
    globalLoadedFonts.add(familyName);
  } else {
    // Style exists, add to cache
    globalLoadedFonts.add(familyName);
  }
};

/**
 * Load the current global custom font if it exists
 */
const loadCurrentGlobalFont = (): void => {
  const currentFontStyle = window?.znRuntime?.theme?.fontStyle;
  if (currentFontStyle) {
    const customFontFamilies = getCustomFontFamilies();
    if (customFontFamilies[currentFontStyle]) {
      loadCustomFontGlobally(currentFontStyle);
    }
  }
};

// Initialize font loading when DOM is ready
const initFontLoader = (): void => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadCurrentGlobalFont);
  } else {
    loadCurrentGlobalFont();
  }
};

// Run initialization immediately
initFontLoader();

export default {};
